/* eslint-disable react-native/no-inline-styles */
import * as React from "react"
import { ViewStyle } from "react-native"
import { observer } from "mobx-react-lite"
import { Cycle, Farm, Pond } from "../../database"
import { FormControlInline, FormControlInlineProps } from "../form-control/form-control-inline"
import { color } from "../../theme"
import _ from "lodash"
import { TxKeyPath, translate } from "../../i18n"
import { useStores } from "../../models"
import { FarmStoreModel } from "../../models/app-store/farm-store/farm-store"
import { PondStoreModel } from "../../models/app-store/farm-store/pond-store/pond-store"
import { Icon, Spinner, View } from "native-base"
import moment from "moment"
import { delay } from "../../utils/delay"
import { Text } from "../text/text"
import { ModelPickerInline, ModelPickerInlineProps } from "../model-picker/model-picker-inline"
import { JalaIcon } from "../jala-icon/jala-icon"

const CONTAINER: ViewStyle = {
}

const POND_CONTAINER: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'flex-start',
}

const FORM_DROPDOWN_RIGHT: ViewStyle = {
  height: 32,
  justifyContent: 'center',
}

const FORM_DROPDOWN: ViewStyle = {
  height: 32,
  justifyContent: 'center',
  marginStart: 16,
}

const PICKER_FARM: ViewStyle = {
  maxWidth: 256
}

const PICKER_POND: ViewStyle = {
  maxWidth: 116
}

const PICKER_CYCLE: ViewStyle = {
}

export interface CommonFilterHeaderProps {
  /**
   * An optional style override useful for padding & margin.
   */
  viewStyle?: ViewStyle
  colorScheme?: "light" | "dark"
  onFarmChange?: (value: any) => void
  onPondChange?: (value: any) => void
  onCycleChange?: (value: any) => void
}

const ModelField = (
  { name, colorScheme, formControlProps, modelPickerProps }:
  { name?: string, colorScheme?: "light" | "dark", formControlProps?: FormControlInlineProps, modelPickerProps?: (Omit<ModelPickerInlineProps, "onValueChange"> & { onValueChange?: (value, index, item) => void }) },
) => (
  <FormControlInline
    labelTx={`waterQualityMultiMeasurementForm.${_.camelCase(name)}` as TxKeyPath}
    colorScheme={colorScheme}
    labelColor={color.palette.gray700}
    style={FORM_DROPDOWN}
    {...formControlProps}
  >
    <ModelPickerInline
      colorScheme={colorScheme}
      placeholderTx={`waterQualityMultiMeasurementForm.${_.camelCase(name)}Placeholder` as TxKeyPath}
      buttonStyle={{backgroundColor: color.palette.white}}
      style={{flex: 1}}
      {...modelPickerProps}
    />
  </FormControlInline>
)

const PickerItem = (
  { name, colorScheme, style, isDisabled }:
  { name?: string, colorScheme?: "light" | "dark", style?: ViewStyle, isDisabled?: boolean },
) => {
  return (
    <View style={style}>
      <Text
        ellipsizeMode="tail"
        numberOfLines={1}
        colorScheme={colorScheme}
        opacity={isDisabled ? 50 : 100}
        text={name || ""} preset="title4" />
    </View>
  )
}

/**
 * Describe your component here
 */
export const CommonFilterHeader = observer(function CommonFilterHeader(props: CommonFilterHeaderProps) {
  const {
    viewStyle, colorScheme, onFarmChange: onFarmChangeProps,
    onPondChange: onPondChangeProps, onCycleChange: onCycleChangeProps } = props

  const { appStore, appStore: { farmId, farmStore } } = useStores();
  const { initiating, pondStore, pondId } = farmStore || FarmStoreModel.create({});
  const { cycleId } = pondStore || PondStoreModel.create({});

  React.useEffect(() => {
    if (pondStore) pondStore.initWithLastCycle()
  },[pondStore])

  const onFarmChange = React.useCallback(async (value) => {
    appStore.set('farmId', value || null)
    onFarmChangeProps && onFarmChangeProps(value)
    await delay(1)
    if (appStore.farmStore) appStore.farmStore.initWithLastBatchAndWaterQualityMultiMeasurements()
  }, [appStore, onFarmChangeProps])

  const onPondChange = React.useCallback(async (value) => {
    farmStore.set('pondId', value || null);
    onPondChangeProps && onPondChangeProps(value)
    await delay(1);
    if (farmStore) farmStore.initWithLastBatchAndWaterQualityMultiMeasurements();
  }, [farmStore, onPondChangeProps])

  const onCycleChange = React.useCallback(async (value) => {
    pondStore.set('cycleId', value);
    onCycleChangeProps && onCycleChangeProps(value)
    await delay(1);
    if (pondStore.cycleId) pondStore.initWithLastCycle();
  }, [pondStore, onCycleChangeProps])

  const renderItemTextPond = React.useCallback((item) => (
    <View flexDirection="row" alignItems="center">
      {item.pro && <Icon marginRight="10px" as={JalaIcon} name="premium-1" size="20px" color={color.palette.orange500} />}
      <Text colorScheme={colorScheme} preset={pondId === item.id ? "title4" : "body2"} text={item.name || ""} />
    </View>
  ), [colorScheme, pondId])

  return (
    <View style={{...CONTAINER, ...viewStyle}}>
      <ModelField
        colorScheme={colorScheme}
        name='farm_id'
        modelPickerProps={{
          Model: Farm,
          value: farmId,
          onValueChange: onFarmChange,
          isDisabled: initiating,
          InputRightElement: initiating && <Spinner ml="8px" mr="9px"></Spinner>,
          titleTx: "cycleForm.farmIdPlaceholder",
          renderSelectedItem(item) {
            return <PickerItem
              isDisabled={initiating}
              style={PICKER_FARM}
              name={item ? item?.name : translate("cycleForm.farmIdPlaceholder")} colorScheme={colorScheme} />
          },
          isUseSearch: true
        }}
      />
      <View style={POND_CONTAINER}>
        <View>
          <ModelField
            key={`${farmId}-${pondStore?.pondId || 0}-pond_id`}
            colorScheme={colorScheme}
            name="pond_id"
            formControlProps={{
              labelTx: 'waterQualityMultiMeasurementForm.pondId', isDisabled: (initiating || !farmId)
            }}
            modelPickerProps={{
              Model: Pond,
              dataLabel: "name",
              dataKey: "id",
              fetchData: (model, search) => (
                Promise.resolve(_.orderBy(
                  (farmStore?.pondStoreModels || []).map(pondStore => pondStore.pond)
                )).then((models) => models.filter((m) => (
                    `${m.name}`
                  ).toLowerCase().includes((search || '').toLowerCase())
                ))
              ),
              renderItemText: renderItemTextPond,
              renderSelectedItem(item) {
                return <PickerItem
                  style={PICKER_POND}
                  isDisabled={(initiating || !farmId)}
                  name={item ? `${item?.name}` : translate("cycleForm.pondIdPlaceholder")}
                  colorScheme={colorScheme} />
              },
              value: !!pondId && (pondId || null),
              isDisabled: (initiating || !farmId),
              onValueChange: onPondChange,
              titleTx: "cycleForm.pondIdPlaceholder",
              InputRightElement: initiating && <Spinner ml="8px" mr="9px"></Spinner>
            }}
          />
        </View>
        <View marginRight={"16px"}>
          <ModelField
            key={`${farmId}-${pondStore?.pondId || 0}-${pondId}-cycle_id`}
            colorScheme={colorScheme}
            name="cycle_id"
            formControlProps={{
              labelTx: 'cycle', isDisabled: !pondId,
              style: FORM_DROPDOWN_RIGHT, flex: 1
            }}
            modelPickerProps={{
              Model: Cycle,
              dataLabel: "started_at",
              fetchData: (model, search) => (
                model.fetch({
                  pond_id__eq: pondId,
                  sort: 'started_at,desc|pond_name_alpha_numeric'
                }).then((models) => models.filter((m) => (
                  `${moment(m.started_at).format("DD MMM YYYY")}${m.finished_at ? ` (${translate("Finished")})` : ""}`
                ).toLowerCase().includes(search.toLowerCase())))
              ),
              renderItemText: (item) => item && `${moment(item.started_at).format("DD MMM YYYY")}${item.finished_at ? ` (${translate("Finished")})` : ""}`,
              renderSelectedItem(item) {
                return <PickerItem
                  style={PICKER_CYCLE}
                  name={item ? `${moment(item.started_at).format("DD MMM YYYY")}${item.finished_at ? ` (${translate("Finished")})` : ""}` : translate("cycleForm.idPlaceholder")}
                  colorScheme={colorScheme}
                  isDisabled={(initiating || !pondId)} />
              },
              value: cycleId && (cycleId || null),
              isDisabled: (initiating || !pondId),
              onValueChange: onCycleChange,
              titleTx: "cycleForm.idPlaceholder",
              placeholderTx: 'cycleForm.idPlaceholder',
              InputRightElement: initiating && <Spinner ml="8px" mr="9px"></Spinner>,
            }}
          />
        </View>
      </View>
    </View>
  )
})
